import React, {useContext, useLayoutEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import InfoIcon from '@material-ui/icons/Info';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import BarChartIcon from '@material-ui/icons/BarChart';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';
import ExtensionIcon from '@material-ui/icons/Extension';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import List from '@material-ui/core/List';
import Collapse from '@material-ui/core/Collapse';
import {Divider} from '@material-ui/core';
import {withRouter} from 'react-router';
import {useLocation} from 'react-router-dom';
import {AuthContext} from '../../../context/auth';
import {getPlanStage} from '../../../utils/productAccess';

const ORANGE = '#FF6900';
const GREY = '#A5A5A5';

const OVERVIEW_BASE = '/dq_pro/school-dashboard/overview';

// School Dashboard nav — mirrors PartnerDashboardList's data-driven pattern.
// Each leaf's `packages` is the P1 School Dashboard spreadsheet's Order/Packages
// column (P0 / P1 / LC); a leaf with no `packages` is common to all three.
const SCHOOL_DASHBOARD_NAV = [
    {
        code: 'introduction',
        label: 'Introduction',
        icon: InfoIcon,
        base: OVERVIEW_BASE,
        children: [
            {label: 'About COS Readiness Solution',  path: `${OVERVIEW_BASE}/about-readiness-solution`, packages: ['P1']},
            {label: 'About COS Children Assessment', path: `${OVERVIEW_BASE}/about-assessment`,          packages: ['P0', 'P1']},
            // "About Scores" holds two tabs gated individually inside AboutScores.page.jsx
            // (COS Readiness Solution Scores = P1 only, COS Children Assessment Scores =
            // P0/P1) — the nav entry itself is visible to the union, P0 or P1.
            {label: 'About Scores',                  path: `${OVERVIEW_BASE}/about-scores`,               packages: ['P0', 'P1']},
            {label: 'About School Report',           path: `${OVERVIEW_BASE}/about-report`,               packages: ['P0', 'P1']},
        ],
    },
    {
        code: 'baseline_results',
        label: 'Baseline Results',
        icon: BarChartIcon,
        children: [
            {label: 'Overview',           path: '/dq_pro/school-dashboard/executive-summary', packages: ['P0', 'P1']},
            {label: 'Readiness',          path: '/dq_pro/school-dashboard/readiness',          packages: ['P0', 'P1']},
            {label: 'Cyber Risks',        path: '/dq_pro/school-dashboard/cyber-risks',        packages: ['P0', 'P1']},
            {label: 'Context & Support',  path: '/dq_pro/school-dashboard/context-support',    packages: ['P0', 'P1']},
            {label: 'Participation',      path: '/dq_pro/school-dashboard/participation',      packages: ['P0', 'P1']},
        ],
    },
    {
        code: 'priority_actions',
        label: 'Priority Actions',
        icon: PriorityHighIcon,
        children: [
            {label: 'Learning Program',    path: '/dq_pro/school-dashboard/priority-actions/learning-program',    packages: ['P0', 'P1']},
            {label: 'System Intervention', path: '/dq_pro/school-dashboard/priority-actions/system-intervention', packages: ['P0', 'P1']},
        ],
    },
    {
        code: 'learning_programs',
        label: 'Learning Programs',
        icon: ExtensionIcon,
        children: [
            {label: 'Learning Deployment', path: '/dq_pro/school-dashboard/learning-programs/learning-deployment', packages: ['P1', 'LC']},
            {label: 'Learning Impact',     path: '/dq_pro/school-dashboard/learning-programs/learning-impact',     packages: ['P1', 'LC']},
        ],
    },
    {
        code: 'reassessment',
        label: 'Reassessment',
        icon: TrendingUpIcon,
        path: '/dq_pro/school-dashboard/reassessment',
        packages: ['P1'],
    },
    {
        code: 'methodology',
        label: 'Methodology',
        icon: MenuBookIcon,
        children: [
            {label: 'How the Assessment Is Scored',       path: '/dq_pro/school-dashboard/methodology',                  packages: ['P0', 'P1']},
            {label: 'How Priority Actions Are Triggered',  path: '/dq_pro/school-dashboard/methodology/priority-actions', packages: ['P0', 'P1']},
            {label: 'How Learning Impact Is Measured',     path: '/dq_pro/school-dashboard/methodology/learning-impact',  packages: ['P1', 'LC']},
        ],
    },
];

const useStyles = makeStyles(() => ({
    item: {
        width: '92%',
        margin: '2px 10px',
        borderRadius: '10px',
        padding: '10px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    itemCollapsed: {
        width: '65%',
    },
    itemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    backItem: {
        backgroundColor: '#000000 !important',
        '& .MuiTypography-root': {color: '#ffffff !important'},
        '&:hover': {backgroundColor: '#222222 !important'},
    },
    subItem: {
        width: '85%',
        margin: '2px 10px 2px 32px',
        borderRadius: '10px',
        padding: '8px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    subItemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    icon: {minWidth: 'unset', marginRight: '12px'},
    label: {
        '& .MuiTypography-root': {fontSize: '14px', fontWeight: 600, color: 'inherit'},
    },
    divider: {margin: '4px 10px'},
}));

/*
 * Every route mounts its own MainLayout (each Route renders
 * <Layout><Component/></Layout>), so this list is rebuilt on each click and its
 * scroll position used to reset to the top — clicking a lower item (e.g.
 * Learning Programs) jumped the sidebar back up. The position is kept here,
 * outside the component, and restored when the list mounts again.
 *
 * The element that actually scrolls is not this list: the Drawer's paper is
 * 100vh with MUI's default overflowY auto, and this list never gets a
 * constrained height, so the paper is what overflows. Listening on the list
 * itself therefore never saw a scroll at all. Walk up to the ancestor that is
 * really scrolling instead of assuming which one it is.
 */
let savedScrollTop = 0;

const findScroller = (start) => {
    for (let el = start; el; el = el.parentElement) {
        const {overflowY} = window.getComputedStyle(el);
        if (/(auto|scroll|overlay)/.test(overflowY) && el.scrollHeight > el.clientHeight + 1) return el;
    }
    return start;
};

const SchoolDashboardList = ({history, isDrawerOpen}) => {
    const scrollRef = useRef(null);
    useLayoutEffect(() => {
        const el = findScroller(scrollRef.current);
        if (!el) return undefined;
        el.scrollTop = savedScrollTop;
        const remember = () => { savedScrollTop = el.scrollTop; };
        el.addEventListener('scroll', remember, {passive: true});
        return () => el.removeEventListener('scroll', remember);
    }, []);
    const classes = useStyles();
    const location = useLocation();
    const {authUser} = useContext(AuthContext);
    const pkg = getPlanStage(authUser);
    // TEMP: plan gating disabled for review — restore `!item.packages || item.packages.includes(pkg)` before shipping.
    const hasAccess = (item) => !item.packages || item.packages.includes(pkg);

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    const visibleGroups = SCHOOL_DASHBOARD_NAV
        .map((group) => group.children
            ? {...group, children: group.children.filter(hasAccess)}
            : group)
        .filter((group) => (group.children ? group.children.length > 0 : hasAccess(group)));

    const [openGroups, setOpenGroups] = useState(() => {
        const initial = {};
        visibleGroups.forEach((group) => {
            if (group.children) {
                initial[group.code] = group.children.some((c) => isActive(c.path));
            }
        });
        return initial;
    });
    const toggleGroup = (code) => setOpenGroups((prev) => ({...prev, [code]: !prev[code]}));

    const NavItem = ({label, icon: Icon, path}) => {
        const active = isActive(path);
        return (
            <>
                <ListItem
                    button
                    onClick={() => history.push(path)}
                    className={`${classes.item} ${!isDrawerOpen ? classes.itemCollapsed : ''} ${active ? classes.itemActive : ''}`}
                >
                    <ListItemIcon className={classes.icon}>
                        <Icon style={{color: active ? '#ffffff' : ORANGE, fontSize: '22px'}}/>
                    </ListItemIcon>
                    {isDrawerOpen && <ListItemText className={classes.label} primary={label}/>}
                </ListItem>
                <Divider className={classes.divider}/>
            </>
        );
    };

    const SubItem = ({label, path}) => {
        // Exact match only — startsWith would also match a shorter sibling
        // path that happens to be a string prefix of this one (e.g.
        // "…/methodology" is a prefix of "…/methodology/priority-actions"),
        // lighting up two sub-items at once.
        const active = location.pathname === path;
        return (
            <ListItem
                button
                onClick={() => history.push(path)}
                className={`${classes.subItem} ${active ? classes.subItemActive : ''}`}
            >
                {isDrawerOpen && <ListItemText className={classes.label} primary={label}/>}
            </ListItem>
        );
    };

    const NavGroup = ({group}) => {
        const active = group.children.some((c) => isActive(c.path));
        const open = !!openGroups[group.code];
        const Icon = group.icon;
        return (
            <>
                <ListItem
                    button
                    onClick={() => {
                        // Clicking the parent both opens it and selects its
                        // first child — otherwise the header never highlights
                        // until a sub-item is clicked separately.
                        if (isDrawerOpen) toggleGroup(group.code);
                        history.push(group.children[0].path);
                    }}
                    className={`${classes.item} ${!isDrawerOpen ? classes.itemCollapsed : ''} ${active ? classes.itemActive : ''}`}
                >
                    <ListItemIcon className={classes.icon}>
                        <Icon style={{color: active ? '#ffffff' : ORANGE, fontSize: '22px'}}/>
                    </ListItemIcon>
                    {isDrawerOpen && (
                        <>
                            <ListItemText className={classes.label} primary={group.label}/>
                            {open
                                ? <ExpandLess style={{color: active ? '#ffffff' : ORANGE}}/>
                                : <ExpandMore style={{color: active ? '#ffffff' : ORANGE}}/>
                            }
                        </>
                    )}
                </ListItem>
                <Divider className={classes.divider}/>

                <Collapse in={open && isDrawerOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {group.children.map((c) => (
                            <SubItem key={c.path} label={c.label} path={c.path}/>
                        ))}
                    </List>
                </Collapse>
            </>
        );
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden'}}>
            <List
                ref={scrollRef}
                style={{flexGrow: 1, paddingTop: '8px', overflowY: 'auto'}}
            >
                {/* Back to Workspace */}
                <ListItem
                    button
                    onClick={() => history.push('/my_workspace')}
                    className={`${classes.item} ${classes.backItem} ${!isDrawerOpen ? classes.itemCollapsed : ''}`}
                >
                    <ListItemIcon className={classes.icon}>
                        <ArrowBackIcon style={{color: '#ffffff', fontSize: '22px'}}/>
                    </ListItemIcon>
                    {isDrawerOpen && <ListItemText className={classes.label} primary="Back to Workspace"/>}
                </ListItem>
                <Divider className={classes.divider}/>

                {visibleGroups.map((group) => group.children
                    ? <NavGroup key={group.code} group={group}/>
                    : <NavItem key={group.code} label={group.label} icon={group.icon} path={group.path}/>
                )}
            </List>
        </div>
    );
};

export default withRouter(SchoolDashboardList);
